import moment from 'moment';

export interface PeriodRestriction {
  period: string;
  minDuration: number;
}

export const PERIOD_RESTRICTIONS: PeriodRestriction[] = [
  { period: 'lunar', minDuration: 1 },
  { period: 'trimestrial', minDuration: 3 },
  { period: 'semestrial', minDuration: 6 },
  { period: 'anual', minDuration: 12 },
  { period: 'la cerere', minDuration: 12 },
];

// Status names that indicate expired or archived contracts
export const EXPIRED_ARCHIVED_STATUSES = ['Expirat/Arhivat', 'Arhivat', 'Expirat', 'expirat', 'arhivat'];

/**
 * Calculate the duration of a contract in months
 * @param dateStart - Contract start date
 * @param dateEnd - Contract end date
 * @returns Duration in months
 */
export const calculateContractDuration = (dateStart: string, dateEnd: string): number => {
  if (!dateStart || !dateEnd) return 0;

  const start = moment(dateStart);
  const end = moment(dateEnd);

  if (!start.isValid() || !end.isValid()) return 0;

  return end.diff(start, 'months', true);
};

/**
 * Get available periods based on contract duration
 * @param dateStart - Contract start date
 * @param dateEnd - Contract end date
 * @returns Array of available periods
 */
export const getAvailablePeriods = (dateStart: string, dateEnd: string): string[] => {
  const duration = calculateContractDuration(dateStart, dateEnd);

  return PERIOD_RESTRICTIONS.filter((restriction) => duration >= restriction.minDuration).map(
    (restriction) => restriction.period
  );
};

/**
 * Check if a specific period is allowed for the given contract duration
 * @param period - The period to check
 * @param dateStart - Contract start date
 * @param dateEnd - Contract end date
 * @returns True if the period is allowed
 */
export const isPeriodAllowed = (period: string, dateStart: string, dateEnd: string): boolean => {
  const duration = calculateContractDuration(dateStart, dateEnd);
  const restriction = PERIOD_RESTRICTIONS.find((r) => r.period === period);

  return restriction ? duration >= restriction.minDuration : false;
};

/**
 * Check if a contract status indicates expired or archived
 * @param status - Contract status object or status name
 * @returns True if the status is expired or archived
 */
export const isExpiredOrArchivedStatus = (status: any): boolean => {
  if (!status) return false;

  const statusName = typeof status === 'string' ? status : status?.name;

  if (!statusName) return false;

  return EXPIRED_ARCHIVED_STATUSES.some((expiredStatus) =>
    statusName.toLowerCase().includes(expiredStatus.toLowerCase())
  );
};
